import { ApiClient } from '../ApiClient';
import { ISubscriptionRepository, CompanySubscriptionStatus } from '../../domain/subscription/ISubscriptionRepository';
import { Resource } from '../../domain/base/Resource';
import {
  Subscription,
  SubscriptionPlan,
  UsageLimits,
  CreateSubscriptionRequest,
  UpdateSubscriptionRequest,
  ReportUsageRequest,
  PaymentMethod,
} from '../../domain/subscription/models/SubscriptionPlan';
import { CreateCheckoutSessionRequest, CheckoutSessionData } from '../../domain/subscription/ISubscriptionRepository';
import { SubscriptionMapper } from './mappers/SubscriptionMapper';
import {
  SubscriptionDTO,
  SubscriptionProductDTO,
  UsageLimitsDTO,
  CheckoutSessionDataDTO,
  PaymentMethodDTO,
} from './dto/SubscriptionDTO';

export class SubscriptionRepository implements ISubscriptionRepository {
  constructor(private apiClient: ApiClient) { }

  async getPlans(): Promise<Resource<SubscriptionPlan[]>> {
    try {
      const response = await this.apiClient.get<SubscriptionProductDTO[]>('/subscriptions/products');
      const plans = response.data.map(dto => SubscriptionMapper.toDomainSubscriptionPlan(dto));
      return {
        status: 'success',
        data: plans,
      };
    } catch (error: any) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: error.response?.data?.message || 'Failed to fetch subscription plans',
        },
      };
    }
  }

  async getCurrentSubscription(): Promise<Resource<Subscription>> {
    try {
      const response = await this.apiClient.get<SubscriptionDTO>('/subscriptions/current');
      const subscription = SubscriptionMapper.toDomainSubscription(response.data);
      return {
        status: 'success',
        data: subscription,
      };
    } catch (error: any) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: error.response?.data?.message || 'Failed to fetch current subscription',
        },
      };
    }
  }

  async createSubscription(request: CreateSubscriptionRequest): Promise<Resource<Subscription>> {
    try {
      const dto = SubscriptionMapper.toDTOCreateSubscriptionRequest(request);
      const response = await this.apiClient.post<SubscriptionDTO>('/subscriptions', dto);
      const subscription = SubscriptionMapper.toDomainSubscription(response.data);
      return {
        status: 'success',
        data: subscription,
      };
    } catch (error: any) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: error.response?.data?.message || 'Failed to create subscription',
        },
      };
    }
  }

  async updateSubscription(id: string, request: UpdateSubscriptionRequest): Promise<Resource<Subscription>> {
    try {
      const dto = SubscriptionMapper.toDTOUpdateSubscriptionRequest(request);
      const response = await this.apiClient.put<SubscriptionDTO>(`/subscriptions/${id}`, dto);
      const subscription = SubscriptionMapper.toDomainSubscription(response.data);
      return {
        status: 'success',
        data: subscription,
      };
    } catch (error: any) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: error.response?.data?.message || 'Failed to update subscription',
        },
      };
    }
  }

  async getUsageLimits(): Promise<Resource<UsageLimits>> {
    try {
      const response = await this.apiClient.get<UsageLimitsDTO>('/subscriptions/limits');
      const limits = SubscriptionMapper.toDomainUsageLimits(response.data);
      return {
        status: 'success',
        data: limits,
      };
    } catch (error: any) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: error.response?.data?.message || 'Failed to fetch usage limits',
        },
      };
    }
  }

  async reportUsage(request: ReportUsageRequest): Promise<Resource<void>> {
    try {
      const dto = SubscriptionMapper.toDTOReportUsageRequest(request);
      await this.apiClient.post('/subscriptions/usage', dto);
      return {
        status: 'success',
        data: undefined,
      };
    } catch (error: any) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: error.response?.data?.message || 'Failed to report usage',
        },
      };
    }
  }

  async createCheckoutSession(request: CreateCheckoutSessionRequest): Promise<Resource<CheckoutSessionData>> {
    try {
      const dto = SubscriptionMapper.toDTOCreateCheckoutSessionRequest(request);
      const response = await this.apiClient.post<CheckoutSessionDataDTO>('/subscriptions/stripe/checkout-session', dto);
      const sessionData = SubscriptionMapper.toDomainCheckoutSessionData(response.data);
      return {
        status: 'success',
        data: sessionData,
      };
    } catch (error: any) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: error.response?.data?.message || 'Failed to create checkout session',
        },
      };
    }
  }

  async startShopifyTrial(): Promise<Resource<void>> {
    try {
      await this.apiClient.post('/subscriptions/shopify/start-trial');
      return { status: 'success', data: undefined };
    } catch (error: any) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: error.response?.data?.message || 'Failed to start Shopify trial',
        },
      };
    }
  }

  async startShopifyCheckout(productId: string, connectionId?: string): Promise<Resource<{ confirmationUrl: string }>> {
    try {
      const response = await this.apiClient.post<{ confirmationUrl: string }>('/subscriptions/shopify/checkout', {
        productId,
        connectionId,
      });
      return { status: 'success', data: response.data };
    } catch (error: any) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: error.response?.data?.message || 'Failed to start Shopify checkout',
        },
      };
    }
  }

  async createPortalSession(): Promise<Resource<{ url: string }>> {
    try {
      const response = await this.apiClient.post<string>('/subscriptions/stripe/portal-session');
      return {
        status: 'success',
        data: { url: response.data },
      };
    } catch (error: any) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: error.response?.data?.message || 'Failed to create portal session',
        },
      };
    }
  }

  async getPaymentMethods(): Promise<Resource<PaymentMethod[]>> {
    try {
      const response = await this.apiClient.get<PaymentMethodDTO[]>('/subscriptions/stripe/payment-methods');
      return {
        status: 'success',
        data: response.data.map(SubscriptionMapper.toDomainPaymentMethod),
      };
    } catch (error: any) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: error.response?.data?.message || 'Failed to get payment methods',
        },
      };
    }
  }

  async getSubscriptionStatuses(companyIds: string[]): Promise<Resource<CompanySubscriptionStatus[]>> {
    try {
      const response = await this.apiClient.get<CompanySubscriptionStatus[]>(
        `/subscriptions/statuses?companyIds=${companyIds.join(',')}`
      );
      return { status: 'success', data: response.data };
    } catch (error: any) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: error.response?.data?.message || 'Failed to get subscription statuses',
        },
      };
    }
  }
}
